import * as pageActions from 'client/actions/pageActions'
import { Select } from 'client/components/core/Sidebar/components/Settings/components'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { UrlRedirect } from 'client/components/core/Sidebar/components/Settings/options'
import { mimeTypes } from 'client/constants/editorSettings'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { usePage } from 'client/store'
import { toggleAutomationRulePreventSaving } from 'client/store/automationRules/automationRulesActions'
import { DataFile } from 'client/store/files/filesReducer'
import pageSelectors from 'client/store/page/pageSelectors'
import { blogPageTypes, pageTypes } from 'common/constants/pageTypes'
import { buttonActionsTypes } from 'common/constants/settings'
import {
  RedirectionTypesEnum,
  sendFormRedirectionChoices,
} from 'common/enums/ButtonActionEnum'
import {
  ButtonActionTypeEnum,
  buttonActionsNames,
  getButtonActions,
} from 'common/enums/ButtonActionTypeEnum'
import { buttonLinkTypeNames } from 'common/enums/ButtonLinkEnum'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { useActions } from 'common/hooks/useActions'
import { addUrlSchemaToTheLink } from 'common/utils/urlUtils'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { pagesWithAutomationRulesTypes } from 'src/editor/shared/constants/automation-rules'
import { CommonButtonSettings } from 'src/editor/shared/entities/common-button-settings'
import { useThemeColor } from 'src/editor/shared/hooks/use-theme-color'
import { AutomationRulesSettings } from 'src/editor/shared/settings/automation-rules-settings'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { ButtonTypeGroup } from 'src/editor/shared/settings/groups/button-type-group'
import { AutomationRuleTriggersEnum } from 'src/editor/shared/types/automation-rules/triggers-types'
import { ActionButtonEntityInterface } from 'src/shared/entities/button/action-button.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { isWebsitePage } from 'src/shared/utils/is-website-page'
import { FileUpload } from '../../components'
import BlogLinkAction from '../../components/BlogLink'
import { WebsiteLink } from '../../components/website-link'
import { SettingsProps } from '../index'

const ButtonSettingsNew = ({
  entity,
  defaultEntity,
}: SettingsProps<ActionButtonEntityInterface>) => {
  const { t } = useTypedTranslation()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<ActionButtonEntityInterface>()
  const dispatch = useDispatch()
  const popups = usePage(pageSelectors.getWebsiteGlobalOrPagePopupEntities)(
    entity,
  )
  const pageType = usePage(pageSelectors.getPageType)
  const { isTemplate } = usePageRouteDetector()
  const doubleOptIn = usePage(pageSelectors.getDoubleOptIn)
  const toggleDoubleOptInAction = useActions(pageActions.toggleDoubleOptIn)

  const {
    text: { paragraphTextColor },
  } = useThemeColor()
  const updateAction = (action: ActionButtonEntityInterface['action']) => {
    const popup = action !== ButtonActionTypeEnum.ShowPopup ? '' : entity.popup

    const needResetRedirectionType =
      action === ButtonActionTypeEnum.SendForm &&
      entity.action !== ButtonActionTypeEnum.SendForm

    const newRedirectionType = blogPageTypes.includes(pageType)
      ? RedirectionTypesEnum.customUrl
      : isWebsite
        ? RedirectionTypesEnum.none
        : RedirectionTypesEnum.nextStep

    const updatedEntity = {
      ...entity,
      action,
      redirectionType: needResetRedirectionType
        ? newRedirectionType
        : entity.redirectionType,
      popup,
    }

    const needResetFile =
      action === ButtonActionTypeEnum.DownloadFile &&
      entity.action !== ButtonActionTypeEnum.DownloadFile
    if (needResetFile) {
      delete updatedEntity.fileId
    }

    const needResetTagId =
      action !== ButtonActionTypeEnum.SendForm && !!entity.tagId

    if (needResetTagId) {
      delete updatedEntity.tagId
    }

    const needResetBlogLink =
      entity.action === ButtonActionTypeEnum.BlogLink &&
      action !== ButtonActionTypeEnum.BlogLink

    if (needResetBlogLink) {
      updatedEntity.blogLink = ''
    }

    const needResetAutomationRules =
      action !== ButtonActionTypeEnum.SendForm || isWebsite
    // check if we have existing rules
    dispatch(
      toggleAutomationRulePreventSaving({
        entityId: entity.id,
        triggerKey: 'buttonId',
        automationTriggerType,
        preventSaving: needResetAutomationRules,
      }),
    )
    updateEntity(updatedEntity as ActionButtonEntityInterface)
  }

  const toggleDoubleOptIn = () => {
    toggleDoubleOptInAction()
  }

  const removeFileId = () => {
    const updatedEntity = { ...entity }
    delete updatedEntity.fileId
    updateEntity(updatedEntity)
  }

  const updatePopup = (popupId: string) => {
    updateProp('popup')(popupId)
  }

  const toggleNoFollow = (value: boolean) => {
    let noFollow = ''
    if (value) {
      noFollow = 'nofollow'
    }
    updateProp('rel')(noFollow)
  }

  const updateBlogLink = (value: string) => {
    const updatedEntity = {
      ...entity,
      blogLink: value,
    }
    updateEntity(updatedEntity)
  }

  const updateRedirectionType = (redirectionType: RedirectionTypesEnum) => {
    const updatedEntity = {
      ...entity,
      urlRedirect: '',
      redirectionType,
    }
    updateEntity(updatedEntity as ActionButtonEntityInterface)
  }
  const popupChoiceList = popups.reduce(
    (popupChoices, popup) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      ...popupChoices,
      [popup.id]:
        popup.type === EntityTypeEnum.Popup
          ? popup?.title
          : popup?.options?.title,
    }),
    {},
  )

  const isActionSendForm = entity.action === ButtonActionTypeEnum.SendForm
  const isRedirectionTypeCustomUrl =
    entity.redirectionType === RedirectionTypesEnum.customUrl
  const isActionBlogLink = entity.action === ButtonActionTypeEnum.BlogLink
  const { isWebsite } = usePageRouteDetector()
  const pageButtonActions = getButtonActions(pageType)
  const availableButtonActions = pageButtonActions.reduce(
    (actions, action) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      ...actions,
      [action]: buttonActionsNames[action],
    }),
    {} as Record<ButtonActionTypeEnum, string>,
  )

  const redirectionTypeNames = { ...sendFormRedirectionChoices }
  if (blogPageTypes.includes(pageType) || isWebsitePage(pageType)) {
    delete redirectionTypeNames[RedirectionTypesEnum.nextStep]
  }

  const automationTriggerType = blogPageTypes.includes(pageType)
    ? AutomationRuleTriggersEnum.blog_page_form_subscribed
    : AutomationRuleTriggersEnum.form_subscribed

  const isAutomationRulesVisible =
    !isTemplate &&
    pagesWithAutomationRulesTypes.includes(pageType) &&
    isActionSendForm

  useEffect(() => {
    if (
      entity.action === buttonActionsTypes.nextStepRedirection &&
      blogPageTypes.includes(pageType)
    ) {
      updateEntity({
        ...entity,
        action: ButtonActionTypeEnum.SendForm,
        redirectionType: RedirectionTypesEnum.customUrl,
      } as ActionButtonEntityInterface)
    }
  }, [])
  return (
    <React.Fragment>
      {isWebsite && <ButtonTypeGroup entity={entity} />}
      {!!pageButtonActions.length && (
        <Select
          update={updateAction}
          choiceList={availableButtonActions}
          labelText="Button action"
          value={entity.action}
        />
      )}
      {entity.action === buttonActionsTypes.downloadFile && (
        <FileUpload
          fileId={entity.fileId}
          afterUpload={(file: DataFile) => updateProp('fileId')(file.id)}
          removeFile={removeFileId}
          mimeTypes={mimeTypes.all}
        />
      )}
      {entity.action === buttonActionsTypes.showPopup && (
        <Select
          labelText="Popup"
          update={updatePopup}
          choiceList={popupChoiceList}
          firstOption="entity_settings.button.choose_actions"
          value={entity.popup}
          skipTranslation
        />
      )}
      {entity.action === buttonActionsTypes.openUrl && [
        <TextInputControl
          key="url"
          wrapperProps={{
            labelText: 'url',
          }}
          update={updateProp('linkUrl')}
          placeholder={t('entity_settings.button.url_input.placeholder')}
          value={entity.linkUrl}
          onBlur={() =>
            updateProp('linkUrl')(addUrlSchemaToTheLink(entity.linkUrl ?? ''))
          }
          removeWhitespaces
        />,
        <Select
          key="link-type"
          labelText="open in new window"
          update={updateProp('linkTarget')}
          choiceList={buttonLinkTypeNames}
          value={entity.linkTarget}
        />,
        <CheckboxControl
          key="noFollow"
          label="entity_settings.button.no_follow.label"
          tooltipText="entity_settings.button.no_follow.description"
          value={Boolean(entity.rel)}
          update={toggleNoFollow}
        />,
      ]}
      {isActionSendForm && (
        <>
          <Select
            labelText="entity_settings.button.send_form.redirection.label"
            value={entity.redirectionType}
            update={updateRedirectionType}
            dataTestElement="button-redirection-checkbox"
            choiceList={redirectionTypeNames}
          />
          {entity.redirectionType === RedirectionTypesEnum.none && (
            <>
              <TextInputControl
                wrapperProps={{
                  labelText:
                    'entity_settings.button.send_form.no_redirection.title',
                }}
                value={entity.optInSuccessText}
                update={updateProp('optInSuccessText')}
              />
              <ColorPickerControl
                label="text color"
                update={updateProp('optInSuccessTextColor')}
                color={entity.optInSuccessTextColor || paragraphTextColor}
              />
            </>
          )}
        </>
      )}
      {blogPageTypes.includes(pageType) && isActionBlogLink && (
        <React.Fragment>
          <BlogLinkAction
            update={updateBlogLink}
            blogLink={entity.blogLink as string}
            needOptionWrapper
          />
          <Select
            labelText="open in new window"
            update={updateProp('linkTarget')}
            choiceList={buttonLinkTypeNames}
            value={entity.linkTarget}
          />
          <CheckboxControl
            label="entity_settings.button.no_follow.label"
            tooltipText="entity_settings.button.no_follow.description"
            value={Boolean(entity.rel)}
            update={toggleNoFollow}
          />
        </React.Fragment>
      )}
      {isWebsite && entity.action === ButtonActionTypeEnum.WebsitePageLink && (
        <React.Fragment>
          <WebsiteLink
            link={entity.websiteLink}
            update={updateProp('websiteLink')}
            withoutWrapper={false}
            labelText="entity_settings.button.website_links.label"
          />
          <Select
            labelText="open in new window"
            update={updateProp('linkTarget')}
            choiceList={buttonLinkTypeNames}
            value={entity.linkTarget}
          />
          <CheckboxControl
            label="entity_settings.button.no_follow.label"
            tooltipText="entity_settings.button.no_follow.description"
            value={Boolean(entity.rel)}
            update={toggleNoFollow}
          />
        </React.Fragment>
      )}
      {isActionSendForm && isRedirectionTypeCustomUrl && (
        <UrlRedirect
          key="urlRedirect"
          urlRedirect={entity.urlRedirect}
          update={(value: string) =>
            updateProp('urlRedirect')(addUrlSchemaToTheLink(value))
          }
          data-test-element="button-redirection-input"
        />
      )}
      {!isWebsite &&
        isActionSendForm &&
        pageType !== pageTypes.webinarRegistration && (
          <CheckboxControl
            label="entity_settings.button.send_model.double_opt_in.label"
            value={doubleOptIn}
            update={toggleDoubleOptIn}
          />
        )}
      {isAutomationRulesVisible && (
        <OptionWrapper
          labelText="entity_settings.automation_rule.automations"
          className="static"
        >
          <AutomationRulesSettings
            entityId={entity.id}
            triggerKey={'buttonId'}
            triggerType={automationTriggerType}
          />
        </OptionWrapper>
      )}
      <CommonButtonSettings entity={entity} defaultEntity={defaultEntity} />
    </React.Fragment>
  )
}

export default ButtonSettingsNew
